import { assertNever, createLogger, type CharacterActionId, type Vector2 } from '@sorrem/shared';
import type { CleanRenderResult, SoundCommand } from '@sorrem-client/types';
import type { ItemEditorMessage } from './item-editor/item-editor-protocol';
import type { PropEditorModelInfo } from './prop-editor/prop-editor-protocol';
import { DEVICE_LOST_MESSAGE, type GatheringEntranceSpot, type PickedTarget, type RenderWorkerCoreCommand, type RenderWorkerMessage, type RenderWorkerSceneCommand } from './render-config';
import { createPendingCalls } from './pending-calls';

const logger = createLogger('client:render-manager');

export interface RenderWorkerSessionOptions {
    readonly onError?: (message: string) => void;
    /** The worker lost its GPU device and has stopped drawing; `useRenderWorkerSession` answers it by starting a fresh worker (#344). */
    readonly onDeviceLost?: () => void;
    readonly onHoverChange?: (target: PickedTarget | null) => void;
    readonly onPickResult?: (requestId: string, target: PickedTarget | null, groundPosition: Vector2 | null) => void;
    /** See the `pointer-ground` message in render-config.ts. */
    readonly onPointerGround?: (position: Vector2 | null) => void;
    readonly onMapReady?: (tag: string | undefined) => void;
    /** A `preload-models` finished; `tag` is the one it was sent with. */
    readonly onModelsReady?: (tag?: string) => void;
    readonly onCharacterReady?: (entityKey: string) => void;
    readonly onAttackAnimationDuration?: (entityKey: string, durationMs: number) => void;
    readonly onCameraPosition?: (position: { readonly x: number; readonly y: number; readonly z: number }) => void;
    readonly onCharacterLoaded?: (info: { readonly meshCount: number; readonly skeletonCount: number; readonly animationGroupCount: number }) => void;
    readonly onEquipmentAttached?: (count: number) => void;
    readonly onBackdropReady?: () => void;
    readonly onItemFrameLoaded?: (frame: { readonly index: number; readonly key: string; readonly ok: boolean }) => void;
    readonly onAnimationSelected?: (info: { readonly name: string; readonly totalFrames: number; readonly totalTimeSeconds: number; readonly targetedAnimationCount: number }) => void;
    /** Result of a `play-action` command - see the `action-played` message in render-config.ts. */
    readonly onActionPlayed?: (info: {
        readonly action: CharacterActionId;
        readonly clipKey: string | null;
        readonly clipDurationMs: number | null;
        readonly speedRatio: number;
        readonly blendDurationMs: number;
        readonly loop: boolean;
    }) => void;
    readonly onPlaybackProgress?: (frame: number, time: number) => void;
    /** The Item Editor's pick, in the model's own space (see `item-editor-picked`). */
    readonly onItemEditorPicked?: (hit: Extract<ItemEditorMessage, { type: 'item-editor-picked' }>['hit']) => void;
    /** The Item Editor's rendered icon (see `item-editor-icon`). */
    readonly onItemEditorIcon?: (result: CleanRenderResult) => void;
    /** The Item Editor's staged model's flame placement, once loaded (see `item-editor-model-info`). */
    readonly onItemEditorModelInfo?: (info: PropEditorModelInfo | null) => void;
    /** The Prop Editor's pick, in the model file's coordinates (see `prop-editor-picked`). */
    readonly onPropEditorPicked?: (point: readonly [number, number, number] | null) => void;
    /** The Prop Editor's prop's model info, once built (see `prop-editor-model-info`). */
    readonly onPropEditorModelInfo?: (info: PropEditorModelInfo | null) => void;
    /** A sound the scene wants played (see the `sound` message in render-config.ts). */
    readonly onSound?: (command: SoundCommand) => void;
    readonly onCrucibleGatheringSceneReady?: (entrance: GatheringEntranceSpot) => void;
    readonly onCrucibleGatheringJoinAnimationComplete?: (accountId: string, characterId: string) => void;
    readonly onCrucibleGatheringLeaveAnimationComplete?: (accountId: string, characterId: string) => void;
}

/** A scene drawing in a worker (or in process), taking the runtime's commands and `TCommand`, its own. */
export interface RenderWorkerSession<TCommand extends RenderWorkerSceneCommand = RenderWorkerSceneCommand> {
    /** Sends a command to the worker: the runtime's (`RenderWorkerCoreCommand`, see render-config.ts for the pointer/render-option contract) or the scene's. */
    sendCommand(command: RenderWorkerCoreCommand | TCommand): void;
    /**
     * Resizes the worker's drawing buffer - and with it the on-screen canvas,
     * which the worker now owns - to `width`/`height` device pixels. The main
     * thread can no longer set `canvas.width`/`height` itself once the canvas
     * has been transferred, so this is the only way to change it. An initial
     * size matching the canvas at creation time is applied automatically; call
     * this whenever the displayed size (or device pixel ratio) changes to keep
     * frames sharp and pointer-picking math in the coordinate space
     * the page's input capture converts pointer positions into (see
     * `GamePageHost`).
     */
    resize(width: number, height: number): void;
    /**
     * The drawing-buffer size last requested from the worker (the canvas's own
     * size at creation, then each `resize` call). Read this instead of
     * `canvas.width`/`height`, which the main thread no longer owns.
     */
    readonly size: { readonly width: number; readonly height: number };
    /** A developer snapshot (#303): the scene alone, drawn again at `scale` times the canvas size. */
    captureCleanRender(scale: number): Promise<CleanRenderResult>;
    dispose(): void;
}

/** A session's outstanding `capture-clean-render` requests, matched to their `clean-render` answers. */
export interface CleanRenderRequests {
    capture(scale: number): Promise<CleanRenderResult>;
    /** Resolves the request `message` answers; true when it was a `clean-render`, which no callback should see. */
    settle(message: RenderWorkerMessage): boolean;
    /** Fails whatever is still outstanding: the scene is gone. */
    abandon(): void;
}

export function createCleanRenderRequests(send: (command: RenderWorkerCoreCommand) => void): CleanRenderRequests {
    const pending = createPendingCalls<CleanRenderResult>({ ok: false, reason: 'The scene closed before the render finished.' });
    return {
        capture: (scale: number): Promise<CleanRenderResult> => pending.call((requestId) => send({ type: 'capture-clean-render', requestId, scale })),
        settle(message: RenderWorkerMessage): boolean {
            if (message.type !== 'clean-render') {
                return false;
            }
            pending.settle(message.requestId, message.result);
            return true;
        },
        abandon: (): void => pending.abandon(),
    };
}

/** Routes one message from a render scene to the matching session callback. */
export function dispatchRenderWorkerMessage(message: RenderWorkerMessage, options: RenderWorkerSessionOptions): void {
    switch (message.type) {
        case 'hover-change':
            options.onHoverChange?.(message.target);
            return;
        case 'pick-result':
            options.onPickResult?.(message.requestId, message.target, message.groundPosition);
            return;
        case 'pointer-ground':
            options.onPointerGround?.(message.position);
            return;
        case 'map-ready':
            options.onMapReady?.(message.tag);
            return;
        case 'models-ready':
            options.onModelsReady?.(message.tag);
            return;
        case 'character-ready':
            options.onCharacterReady?.(message.entityKey);
            return;
        case 'attack-animation-duration':
            options.onAttackAnimationDuration?.(message.entityKey, message.durationMs);
            return;
        case 'camera-position':
            options.onCameraPosition?.(message.position);
            return;
        case 'character-loaded':
            options.onCharacterLoaded?.({ meshCount: message.meshCount, skeletonCount: message.skeletonCount, animationGroupCount: message.animationGroupCount });
            return;
        case 'equipment-attached':
            options.onEquipmentAttached?.(message.count);
            return;
        case 'backdrop-ready':
            options.onBackdropReady?.();
            return;
        case 'item-frame-loaded':
            options.onItemFrameLoaded?.({ index: message.index, key: message.key, ok: message.ok });
            return;
        case 'animation-selected':
            options.onAnimationSelected?.({ name: message.name, totalFrames: message.totalFrames, totalTimeSeconds: message.totalTimeSeconds, targetedAnimationCount: message.targetedAnimationCount });
            return;
        case 'playback-progress':
            options.onPlaybackProgress?.(message.frame, message.time);
            return;
        case 'item-editor-picked':
            options.onItemEditorPicked?.(message.hit);
            return;
        case 'item-editor-icon':
            options.onItemEditorIcon?.(message.result);
            return;
        case 'item-editor-model-info':
            options.onItemEditorModelInfo?.(message.info);
            return;
        case 'prop-editor-picked':
            options.onPropEditorPicked?.(message.point);
            return;
        case 'prop-editor-model-info':
            options.onPropEditorModelInfo?.(message.info);
            return;
        case 'action-played':
            options.onActionPlayed?.({
                action: message.action,
                clipKey: message.clipKey,
                clipDurationMs: message.clipDurationMs,
                speedRatio: message.speedRatio,
                blendDurationMs: message.blendDurationMs,
                loop: message.loop,
            });
            return;
        case 'error':
            logger.error('render worker reported an error:', message.message);
            options.onError?.(message.message);
            return;
        case 'device-lost':
            logger.warn('render worker lost its GPU device');
            if (options.onDeviceLost) {
                options.onDeviceLost();
            } else {
                // Nothing will start this scene again: say why it went dark.
                options.onError?.(DEVICE_LOST_MESSAGE);
            }
            return;
        case 'clean-render':
            // Answered through the session's `captureCleanRender` (see `createCleanRenderRequests`).
            return;
        case 'sound':
            options.onSound?.(message.command);
            return;
        case 'crucible-gathering-scene-ready':
            options.onCrucibleGatheringSceneReady?.(message.entrance);
            return;
        case 'crucible-gathering-join-animation-complete':
            options.onCrucibleGatheringJoinAnimationComplete?.(message.accountId, message.characterId);
            return;
        case 'crucible-gathering-leave-animation-complete':
            options.onCrucibleGatheringLeaveAnimationComplete?.(message.accountId, message.characterId);
            return;
        default:
            assertNever(message, 'render worker message');
    }
}

/**
 * Main-thread glue for a render worker: starts the worker, hands it
 * `canvas` to render into, forwards errors/hover/pick events to the given
 * callbacks, and exposes `sendCommand` for pushing pointer input or
 * render-option changes in.
 *
 * `canvas` is transferred with `transferControlToOffscreen()`, so the worker
 * draws to it directly and the browser presents the result - the main thread
 * never touches a frame. After the transfer the main thread can no longer set
 * `canvas.width`/`height` or draw on it, and a canvas can only be transferred
 * once, so pass a freshly created element for each session (never one a
 * previous session used). Set the initial `canvas.width`/`height` *before*
 * calling this; use `session.resize` afterwards.
 *
 * Deliberately does not wire any DOM pointer-event listeners itself - 
 * different consumers may want different event sources or throttling on top
 * of `sendCommand`'s latest-wins pointer-move semantics, so this stays a
 * thin, composable primitive rather than a framework.
 *
 * `createWorker` must construct the worker from a form Vite can bundle: a
 * `?worker` import's constructor, or a literal
 * `new Worker(new URL('./x.ts', import.meta.url), { type: 'module' })`. A URL
 * handed in from elsewhere is only an asset to Vite, so production would load
 * the raw TypeScript source.
 */
export function createRenderWorkerSession<TCommand extends RenderWorkerSceneCommand = RenderWorkerSceneCommand>(
    canvas: HTMLCanvasElement,
    createWorker: () => Worker,
    options: RenderWorkerSessionOptions = {},
): RenderWorkerSession<TCommand> {
    let size = { width: canvas.width, height: canvas.height };
    const offscreen = canvas.transferControlToOffscreen();
    const worker = createWorker();

    // Always the first message: constructs the worker's Engine/scene on the
    // transferred canvas at its current size - see render-worker.ts's doc
    // comment on why construction waits for this instead of happening eagerly
    // at some default/guessed size. Worker message delivery is strictly
    // ordered, so sending this synchronously here, before returning the
    // session to the caller, guarantees it's processed before any later
    // `sendCommand`/`resize` call.
    worker.postMessage({ type: 'initialize', canvas: offscreen }, [offscreen]);
    const cleanRenders = createCleanRenderRequests((command) => worker.postMessage(command));

    worker.onmessage = (event: MessageEvent<RenderWorkerMessage>): void => {
        if (!cleanRenders.settle(event.data)) {
            dispatchRenderWorkerMessage(event.data, options);
        }
    };

    worker.onerror = (event: ErrorEvent): void => {
        logger.error('render worker crashed:', event.message);
        options.onError?.(event.message);
    };

    return {
        sendCommand(command: RenderWorkerCoreCommand | TCommand): void {
            worker.postMessage(command);
        },
        resize(width: number, height: number): void {
            size = { width, height };
            worker.postMessage({ type: 'resize', width, height });
        },
        get size(): { readonly width: number; readonly height: number } {
            return size;
        },
        captureCleanRender: (scale) => cleanRenders.capture(scale),
        dispose(): void {
            worker.terminate();
            cleanRenders.abandon();
        },
    };
}
